import { useTranslation } from "react-i18next";
import { Box, Button, IconButton, Skeleton, Tab, Tabs, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ArrowBackButtonEditIcon } from "../../assets/icons/entity-icons";
import { getEntityProfileRoute } from "../../../../routers/routeUtils";
import { EntityBranchDetails } from "../../../Entity/api/types/entityBranches.types";

const TABS = ["facilities", "sports", "workingDays"] as const;
type TabType = (typeof TABS)[number];

const TabsNavigation = ({
  branchData,
  isBranchDataLoading,
}: {
  branchData?: EntityBranchDetails;
  isBranchDataLoading: boolean;
}) => {
  const { t, i18n } = useTranslation("profiles", { keyPrefix: "entityProfileBranch" });
  const navigate = useNavigate();
  const location = useLocation();
  const { entityId } = useParams<{ entityId: string }>();

  const searchParams = new URLSearchParams(location.search);
  const currentTab = searchParams.get("tab") || "facilities";

  const handleTabChange = (event: React.SyntheticEvent, newValue: TabType) => {
    const searchParams = new URLSearchParams(location.search);
    Array.from(searchParams.keys()).forEach((key) => {
      searchParams.delete(key);
    });
    searchParams.set("tab", newValue);

    navigate({ search: searchParams.toString() });
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <IconButton
          sx={styles.backButton}
          onClick={() => navigate(getEntityProfileRoute(Number(entityId)))}
          size="small"
        >
          <ArrowBackButtonEditIcon />
        </IconButton>
        <Box sx={styles.branchInfoContainer}>
          {!isBranchDataLoading && branchData?.name && <Typography sx={styles.name}>{branchData.name}</Typography>}
          {!isBranchDataLoading && branchData?.address && (
            <Typography sx={styles.address}>{branchData.address}</Typography>
          )}
          {isBranchDataLoading && (
            <>
              <Skeleton variant="text" width={150} height={32} />
              <Skeleton variant="text" width={200} height={24} />
            </>
          )}
        </Box>
        <Button
          disabled={isBranchDataLoading || !branchData?.googleMapsLink}
          variant="outlined-md"
          sx={styles.viewOnMapButton}
          component="a"
          href={branchData?.googleMapsLink}
        >
          {t("viewOnMap")}
        </Button>
      </Box>
      <Tabs value={currentTab} onChange={handleTabChange} aria-label="profile tabs" sx={styles.tabs}>
        {TABS.map((tab) => (
          <Tab key={tab} label={t(tab)} value={tab} sx={styles.tabStyle} />
        ))}
      </Tabs>
    </Box>
  );
};

export default TabsNavigation;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "32px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
    padding: "20px 20px 0",
  },
  header: {
    display: "flex",
    gap: "8px",
  },
  backButton: {
    padding: "0px",
    height: "fit-content",
    backgroundColor: "transparent !important",
  },
  branchInfoContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    flex: 1,
  },
  name: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 800,
    lineHeight: "normal",
  },
  address: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
  },
  tabs: {
    minHeight: "fit-content",
    "& .MuiTabs-flexContainer": {
      gap: "24px",
    },
  },
  tabStyle: {
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
    padding: "0px 4px 12px 4px",
    textTransform: "none",
    minHeight: "fit-content",
    minWidth: "fit-content",
    "&.Mui-selected": {
      color: "var(--Brand-700, #076D0D)",
    },
  },
  viewOnMapButton: {
    height: "fit-content",
  },
};
